import React from 'react';
import type { ImageInfoComponent as ImageInfoComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getCroppedImageStyles, hasCropApplied } from './ComponentEditModal/shared/cropUtils';
import { getFontFamily } from '../utils/fontLoader';

interface ImageInfoComponentProps {
  component: ImageInfoComponentType;
}

export const ImageInfoComponent: React.FC<ImageInfoComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Color priority: component.data.customColors > theme colors > fallback
  const backgroundColor =
    data.customColors?.background || themeStyles.imageInfo?.background || '#ffffff';
  const textColor = data.customColors?.text || themeStyles.imageInfo?.text || '#111827';
  const titleColor = data.customColors?.title || themeStyles.imageInfo?.title || '#111827';
  const subtitleColor = data.customColors?.subtitle || themeStyles.imageInfo?.subtitle || '#6b7280';
  const imageBorderColor =
    data.customColors?.imageBorder || themeStyles.imageInfo?.imageBorder || '#e5e7eb';

  // Button colors: component customColors > theme colors > fallback
  const buttonBg = data.customColors?.buttonBg || themeStyles.imageInfo?.buttonBg || '#3b82f6';
  const buttonText =
    data.customColors?.buttonText || themeStyles.imageInfo?.buttonText || '#ffffff';

  // Get button styles using theme colors (inline styles for proper theming)
  const getButtonStyles = (style: string): React.CSSProperties => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBg,
          color: buttonText,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: buttonBg,
          border: 'none',
        };
      default:
        return {
          backgroundColor: buttonBg,
          color: buttonText,
          border: 'none',
        };
    }
  };

  // Determine if background is a gradient or solid color
  const isGradient = backgroundColor.includes('gradient');
  const backgroundStyle: React.CSSProperties = {};
  if (isGradient) {
    backgroundStyle.background = backgroundColor;
  } else {
    backgroundStyle.backgroundColor = backgroundColor;
  }

  // Sort items by order
  const sortedItems = [...(data.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Get image URL
  const getImageUrl = (item: (typeof sortedItems)[0]) => {
    return item.cloudfrontUrl || item.imageUrl || '';
  };

  // Image shape classes
  const getImageShapeClass = () => {
    return data.imageShape === 'round' ? 'rounded-full' : 'rounded-lg';
  };

  // Image size classes
  const getImageSizeClasses = () => {
    switch (data.imageSize || 'medium') {
      case 'small':
        return 'w-32 h-32 md:w-36 md:h-36';
      case 'medium':
        return 'w-40 h-40 md:w-48 md:h-48';
      case 'large':
        return 'w-60 h-60 md:w-72 md:h-72';
      default:
        return 'w-40 h-40 md:w-48 md:h-48';
    }
  };

  // Holistic spacing config — affects section padding, header margin,
  // item gap, and internal element spacing so the difference is unmistakable
  const spacingConfig = {
    compact: {
      section: 'py-6 md:py-8',
      header: 'mb-4 md:mb-6',
      gap: 'gap-3 md:gap-4',
      imageMargin: 'mb-2',
      textMargin: 'mb-1',
      buttonMargin: 'mt-2',
    },
    normal: {
      section: 'py-12 md:py-16 lg:py-24',
      header: 'mb-8 md:mb-12',
      gap: 'gap-6 md:gap-8',
      imageMargin: 'mb-4',
      textMargin: 'mb-2',
      buttonMargin: 'mt-4',
    },
    spacious: {
      section: 'py-16 md:py-24 lg:py-32',
      header: 'mb-12 md:mb-16',
      gap: 'gap-10 md:gap-16',
      imageMargin: 'mb-6',
      textMargin: 'mb-4',
      buttonMargin: 'mt-6',
    },
  };

  const activeSpacing = spacingConfig[data.spacing] || spacingConfig.normal;

  // Layout classes
  const getLayoutClasses = () => {
    switch (data.layout) {
      case 'grid': {
        const gridCols = {
          1: 'grid-cols-1',
          2: 'grid-cols-1 md:grid-cols-2',
          3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
          4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
          5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5',
          6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
        };
        return `grid ${gridCols[data.columns as keyof typeof gridCols]} [&>*]:min-w-0`;
      }
      case 'vertical':
        return 'flex flex-col';
      case 'horizontal':
      default:
        return 'flex flex-wrap';
    }
  };

  // Render single item
  const renderItem = (item: (typeof sortedItems)[0], index: number) => {
    const imageUrl = getImageUrl(item);
    const imageShapeClass = getImageShapeClass();
    const hasEffects = hasCropApplied((item as any).crop);
    const cropStyles = getCroppedImageStyles((item as any).crop);

    // Width classes based on layout
    const itemWidthClass =
      data.layout === 'vertical'
        ? 'w-full'
        : data.layout === 'grid'
          ? ''
          : 'w-full sm:flex-1 sm:min-w-[260px] sm:max-w-md';

    return (
      <div key={item.id} className={`flex flex-col min-w-0 ${itemWidthClass} text-center`}>
        {/* Image */}
        {imageUrl && (
          <div className={`${activeSpacing.imageMargin} flex justify-center`}>
            <div
              className={`${getImageSizeClasses()} ${imageShapeClass} relative overflow-hidden${data.showImageBorder !== false ? ' border-2' : ''}`}
              style={data.showImageBorder !== false ? { borderColor: imageBorderColor } : undefined}
            >
              <img
                src={imageUrl}
                alt={item.alt || `Image ${index + 1}`}
                className={!hasEffects ? 'w-full h-full object-cover' : ''}
                style={hasEffects ? cropStyles : undefined}
              />
            </div>
          </div>
        )}

        {/* Subtitle */}
        {item.subtitle && (
          <h3
            className={`text-xl md:text-2xl font-bold ${activeSpacing.textMargin} break-words`}
            style={{ color: titleColor, ...headingFontStyle }}
            dangerouslySetInnerHTML={{
              __html: renderTextWithLineBreaks(item.subtitle),
            }}
          />
        )}

        {/* Description */}
        {item.description && (
          <p
            className="text-base md:text-lg leading-relaxed break-words"
            style={{ color: textColor, ...bodyFontStyle }}
            dangerouslySetInnerHTML={{
              __html: renderTextWithLineBreaks(item.description),
            }}
          />
        )}

        {/* Button */}
        {item.button && (
          <div className={activeSpacing.buttonMargin}>
            <a
              href={item.button.url}
              target={item.button.target || '_self'}
              rel={item.button.target === '_blank' ? 'noopener noreferrer' : undefined}
              className="inline-block px-6 py-2 rounded-md font-medium transition-opacity hover:opacity-80"
              style={getButtonStyles(item.button.style)}
            >
              {item.button.text}
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className={activeSpacing.section} style={backgroundStyle}>
      <div className="max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8">
        {/* Title and Subtitle */}
        {(data.showTitle || data.showSubtitle) && (
          <div className={`text-center ${activeSpacing.header}`}>
            {data.showTitle && data.title && (
              <h2
                className="text-3xl md:text-4xl font-bold mb-4 break-words"
                style={{ color: titleColor, ...headingFontStyle }}
                dangerouslySetInnerHTML={{
                  __html: renderTextWithLineBreaks(data.title),
                }}
              />
            )}
            {data.showSubtitle && data.subtitle && (
              <p
                className="text-lg md:text-xl break-words"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{
                  __html: renderTextWithLineBreaks(data.subtitle),
                }}
              />
            )}
          </div>
        )}

        {/* Items Container */}
        <div
          className={`${getLayoutClasses()} ${activeSpacing.gap} ${
            data.layout === 'horizontal' ? 'justify-center' : ''
          }`}
        >
          {sortedItems.map((item, index) => renderItem(item, index))}
        </div>
      </div>
    </section>
  );
};
